import { Customer, InputRuleApiModel, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { EoriNumber, TaxId } from '../consts/ItemMetaConstants';

// Address-level customs fields the quoting API returns as lane requirements. When the quote for this
// lane doesn't list a field for a party, any stored value (from a saved address, CSV import or a
// stale earlier customs quote) is dropped so it isn't forwarded to the booker on a lane that doesn't
// need it - carriers reject unexpected customs data (e.g. Royal Mail on a domestic EORI). See
// IBUG-2582. Sender and recipient are gated independently against their own validation.
//
// IOSS is intentionally excluded: quoting never demands it (it isn't a quote requirement), but it is
// accepted on an order when supplied by the selling marketplace or the user's own IOSS account, so it
// must not be stripped when absent from the quote.
const gatedCustomsFields = [EoriNumber, TaxId];

const quoteRequiresField = (validation: InputRuleApiModel[] | undefined, field: string): boolean =>
  !!validation?.some((rule) => rule.name === field);

// Removes any gated customs field the party's validation doesn't require. Clones only when it
// actually changes something, so the live item state is left untouched.
const stripUnrequiredCustoms = (party: Customer, validation: InputRuleApiModel[] | undefined): Customer => {
  if (!party?.Meta) {
    return party;
  }

  const toRemove = gatedCustomsFields.filter(
    (field) => party.Meta[field] !== undefined && !quoteRequiresField(validation, field)
  );

  if (toRemove.length === 0) {
    return party;
  }

  const meta = { ...party.Meta };
  toRemove.forEach((field) => delete meta[field]);
  return { ...party, Meta: meta };
};

// Prepares an item's customs data immediately before it is sent to the verify/prepare endpoints.
// Returns a copy - the live item state is left untouched.
export const preprocessItemForShipping = (item: SmartSendItemApiModel): SmartSendItemApiModel => {
  const quoteState = item.State?.validation?.QuoteState;

  return {
    ...item,
    Sender: stripUnrequiredCustoms(item.Sender, quoteState?.SenderValidation),
    Recipient: stripUnrequiredCustoms(item.Recipient, quoteState?.RecipientValidation)
  };
};
